import { Link, useNavigate, useParams } from "@tanstack/solid-router";
import { Show, createSignal, For } from "solid-js";

import { AccountsBulkBar } from "~/components/AccountsBulkBar";
import { AppShell } from "~/components/AppShell";
import { CsvIoButtons } from "~/components/CsvIoButtons";
import { SavedViewsBar } from "~/components/SavedViewsBar";
import { TagBadges, TagsEditor } from "~/components/TagsEditor";
import { UserPicker } from "~/components/UserPicker";
import { useAccountsInfinite, useCreateAccount, useMe } from "~/lib/hooks";
import { useRowSelection } from "~/lib/selection";
import type { AccountListItem } from "~/lib/types";

export function AccountsPage() {
  const params = useParams({ strict: false }) as () => { wsKey: string };
  const navigate = useNavigate();

  const [q, setQ] = createSignal("");
  const [stage, setStage] = createSignal("");
  const [tagFilter, setTagFilter] = createSignal("");
  const [ownerFilter, setOwnerFilter] = createSignal<string | null>(null);
  const [ownerFilterName, setOwnerFilterName] = createSignal<string | null>(null);
  const [showCreate, setShowCreate] = createSignal(false);
  const [createTags, setCreateTags] = createSignal<string[]>([]);
  const [createOwner, setCreateOwner] = createSignal<string | null>(null);
  const [createOwnerName, setCreateOwnerName] = createSignal<string | null>(null);
  const me = useMe();

  const accounts = useAccountsInfinite(
    () => params().wsKey,
    () => ({
      q: q() || undefined,
      lifecycle_stage: stage() || undefined,
      tag: tagFilter() || undefined,
      owner_user_id: ownerFilter() ?? undefined,
      limit: 50,
    }),
  );

  const visibleRows = () =>
    (accounts.data?.pages ?? []).flatMap((p) => p.items);
  const selection = useRowSelection<AccountListItem>(() =>
    visibleRows().map((r) => r.id),
  );

  const createMutation = useCreateAccount(() => params().wsKey);

  const handleCreate = async (e: SubmitEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const name = String(data.get("name") ?? "").trim();
    if (!name) return;
    const acc = await createMutation.mutateAsync({
      name,
      primary_domain: String(data.get("primary_domain") ?? "") || undefined,
      primary_email: String(data.get("primary_email") ?? "") || undefined,
      kind: String(data.get("kind") ?? "company"),
      lifecycle_stage: String(data.get("lifecycle_stage") ?? "prospect"),
      tags: createTags().length > 0 ? createTags() : undefined,
      owner_user_id: createOwner() ?? undefined,
    });
    setShowCreate(false);
    setCreateTags([]);
    setCreateOwner(null);
    setCreateOwnerName(null);
    void navigate({
      to: "/workspaces/$wsKey/accounts/$accId",
      params: { wsKey: params().wsKey, accId: acc.id },
    });
  };

  return (
    <AppShell
      wsKey={params().wsKey}
      pageTitle="Accounts"
      rightSlot={
        <div class="flex items-center gap-1">
          <CsvIoButtons wsKey={() => params().wsKey} plural="accounts" />
          <button class="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
            New account
          </button>
        </div>
      }
    >
      <div class="flex flex-col gap-4">
        <SavedViewsBar
          wsKey={() => params().wsKey}
          entityType={() => "account"}
          currentFilters={() => ({
            q: q() || undefined,
            lifecycle_stage: stage() || undefined,
            tag: tagFilter() || undefined,
            owner_user_id: ownerFilter() ?? undefined,
            owner_user_name: ownerFilterName() ?? undefined,
          })}
          onApply={(f) => {
            setQ((f.q as string) ?? "");
            setStage((f.lifecycle_stage as string) ?? "");
            setTagFilter((f.tag as string) ?? "");
            setOwnerFilter((f.owner_user_id as string) ?? null);
            setOwnerFilterName((f.owner_user_name as string) ?? null);
          }}
          onClear={() => {
            setQ("");
            setStage("");
            setTagFilter("");
            setOwnerFilter(null);
            setOwnerFilterName(null);
          }}
        />
        <div class="flex gap-2 flex-wrap">
          <input
            class="input input-bordered input-sm w-64"
            placeholder="Search by name, domain, email…"
            aria-label="Search accounts"
            value={q()}
            onInput={(e) => setQ(e.currentTarget.value)}
          />
          <select
            class="select select-bordered select-sm"
            aria-label="Filter by lifecycle stage"
            value={stage()}
            onChange={(e) => setStage(e.currentTarget.value)}
          >
            <option value="">All stages</option>
            <option value="prospect">Prospect</option>
            <option value="qualified">Qualified</option>
            <option value="customer">Customer</option>
            <option value="churned">Churned</option>
          </select>
          <input
            class="input input-bordered input-sm w-40"
            placeholder="Filter by tag"
            aria-label="Filter by tag"
            value={tagFilter()}
            onInput={(e) => setTagFilter(e.currentTarget.value)}
          />
          <Show when={tagFilter()}>
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              onClick={() => setTagFilter("")}
              aria-label="Clear tag filter"
            >
              ✕
            </button>
          </Show>
          <div class="w-60">
            <UserPicker
              wsKey={() => params().wsKey}
              value={ownerFilter}
              selectedName={ownerFilterName}
              onChange={(id, name) => {
                setOwnerFilter(id);
                setOwnerFilterName(name);
              }}
              placeholder="Filter by owner…"
              label="Filter by owner"
              size="sm"
            />
          </div>
          <Show when={me.data?.user_id && me.data.user_id !== ownerFilter()}>
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              onClick={() => {
                setOwnerFilter(me.data!.user_id!);
                setOwnerFilterName(me.data?.display_name ?? me.data?.email ?? "Me");
              }}
            >
              Mine
            </button>
          </Show>
        </div>

        <AccountsBulkBar
          wsKey={() => params().wsKey}
          count={selection.count}
          rows={() => selection.selectedRows(visibleRows())}
          onClear={selection.clear}
        />

        <Show when={accounts.isLoading}>
          <div role="status" aria-live="polite" class="text-center py-8 opacity-60">
            Loading accounts…
          </div>
        </Show>

        <Show when={accounts.isError}>
          <div role="alert" class="alert alert-error">
            Failed to load accounts.
          </div>
        </Show>

        <Show when={accounts.data}>
          {(_data) => (
            <div class="overflow-x-auto bg-base-100 rounded-lg shadow-sm">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th class="w-8">
                      <input
                        type="checkbox"
                        class="checkbox checkbox-sm"
                        aria-label="Select all accounts on this page"
                        checked={selection.allSelected()}
                        ref={(el) => {
                          // SolidJS doesn't pass `indeterminate` through props;
                          // set it imperatively each render.
                          if (el) el.indeterminate = selection.someSelected();
                        }}
                        onChange={(e) =>
                          e.currentTarget.checked
                            ? selection.selectAll()
                            : selection.clear()
                        }
                      />
                    </th>
                    <th>Name</th>
                    <th>Kind</th>
                    <th>Domain</th>
                    <th>Stage</th>
                    <th>Tags</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={visibleRows()}>
                    {(a) => (
                      <tr
                        class={`hover ${selection.has(a.id) ? "bg-base-200" : ""}`}
                      >
                        <td>
                          <input
                            type="checkbox"
                            class="checkbox checkbox-sm"
                            aria-label={`Select ${a.name}`}
                            checked={selection.has(a.id)}
                            onChange={() => selection.toggle(a.id)}
                          />
                        </td>
                        <td>
                          <Link
                            to="/workspaces/$wsKey/accounts/$accId"
                            params={{ wsKey: params().wsKey, accId: a.id }}
                            class="link link-primary"
                          >
                            {a.name}
                          </Link>
                        </td>
                        <td>
                          <span class="badge badge-ghost badge-sm">{a.kind}</span>
                        </td>
                        <td class="text-sm opacity-80">{a.primary_domain ?? "—"}</td>
                        <td>
                          <span class="badge badge-outline badge-sm">{a.lifecycle_stage}</span>
                        </td>
                        <td>
                          <TagBadges tags={() => a.tags} size="xs" />
                        </td>
                        <td class="text-xs opacity-60">
                          {new Date(a.updated_at).toLocaleDateString()}
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
              <Show when={visibleRows().length === 0}>
                <div class="text-center py-12 opacity-60">No accounts match.</div>
              </Show>
            </div>
          )}
        </Show>
        <Show when={accounts.hasNextPage}>
          <div class="flex justify-center py-2">
            <button
              type="button"
              class="btn btn-sm"
              disabled={accounts.isFetchingNextPage}
              onClick={() => accounts.fetchNextPage()}
            >
              {accounts.isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        </Show>
      </div>

      <Show when={showCreate()}>
        <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="new-account-title">
          <div class="modal-box">
            <h3 id="new-account-title" class="font-bold text-lg mb-4">
              New account
            </h3>
            <form onSubmit={handleCreate} class="flex flex-col gap-3">
              <input
                name="name"
                class="input input-bordered"
                placeholder="Name *"
                aria-label="Name"
                required
              />
              <input
                name="primary_domain"
                class="input input-bordered"
                placeholder="Primary domain (acme.example)"
                aria-label="Primary domain"
              />
              <input
                name="primary_email"
                class="input input-bordered"
                placeholder="Primary email"
                aria-label="Primary email"
                type="email"
              />
              <select name="kind" class="select select-bordered" aria-label="Kind">
                <option value="company">Company</option>
                <option value="household">Household</option>
                <option value="government">Government</option>
                <option value="nonprofit">Nonprofit</option>
                <option value="partner">Partner</option>
              </select>
              <select
                name="lifecycle_stage"
                class="select select-bordered"
                aria-label="Lifecycle stage"
              >
                <option value="prospect">Prospect</option>
                <option value="qualified">Qualified</option>
                <option value="customer">Customer</option>
                <option value="churned">Churned</option>
              </select>
              <div>
                <label class="label">
                  <span class="label-text">Tags</span>
                </label>
                <TagsEditor
                  wsKey={() => params().wsKey}
                  entityType="account"
                  value={createTags}
                  onChange={setCreateTags}
                />
              </div>
              <div>
                <label class="label">
                  <span class="label-text">Owner</span>
                </label>
                <UserPicker
                  wsKey={() => params().wsKey}
                  value={createOwner}
                  selectedName={createOwnerName}
                  onChange={(id, name) => {
                    setCreateOwner(id);
                    setCreateOwnerName(name);
                  }}
                />
              </div>
              <div class="modal-action">
                <button
                  type="button"
                  class="btn btn-ghost"
                  onClick={() => {
                    setShowCreate(false);
                    setCreateTags([]);
                    setCreateOwner(null);
                    setCreateOwnerName(null);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Saving…" : "Create"}
                </button>
              </div>
              <Show when={createMutation.isError}>
                <div class="alert alert-error text-sm">
                  {(createMutation.error as Error)?.message ?? "Failed"}
                </div>
              </Show>
            </form>
          </div>
        </dialog>
      </Show>
    </AppShell>
  );
}
